import React, { useEffect, useState } from "react";
import { Col, Row, Pagination } from "react-bootstrap";
import FilterSec from "../component/FilterSec";
import "../assets/css/productlist.css";
import ProductCard from "../component/ProductCard";
import axios from "axios";
import { useFilter } from "../component/VerifyEmail";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Box from "@mui/material/Box";
import Slider from "@mui/material/Slider";
import ProductInquiry from "../component/ProductInquiry";
import CreateCatalogBtn from "../component/CreateCatalogBtn"
import { FiPlus, FiMinus, FiFilter, FiX } from "react-icons/fi";
import { Link, useSearchParams } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { toast, ToastContainer } from "react-toastify";
import { Puff } from "react-loader-spinner";

function valuetext(value) {
  return `${value}°C`;
}

const ProductList = (categoryName) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [openProductId, setOpenProductId] = useState(null);

  // Metadata for filter labels/ranges (fetched once)
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubcategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [minVal, setMinVal] = useState(0);
  const [maxVal, setMaxVal] = useState(50000);
  const [priceRange, setPriceRange] = useState([0, 0]);
  const [values, setValue] = useState([0, 50000]);
  const [priceRanges, setPriceRanges] = useState([]);
  // UI State
  const [expanded, setExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [show, setShow] = useState(false); // Mobile filter toggle
  const isMobile = useMediaQuery({ maxWidth: 991 });
  const isExtraSmallMobile = useMediaQuery({ maxWidth: 320 });
  const per_page = 12;
  const [categoryBrands, setCategoryBrands] = useState([]);
  const [categorySubCategories, setCategorySubCategories] = useState([]);
  const lastScopeRef = React.useRef({ category: "", search: "", subCategory: "" });

  // Data State
  const [products, setProducts] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [activePage, setActivePage] = useState(1);

  // Parsed Filter State (Derived or set from URL)
  const [activeCategories, setActiveCategories] = useState([]);
  const [activeSubCategories, setActiveSubCategories] = useState([]);
  const [activeBrands, setActiveBrands] = useState([]);

  // Fetch metadata once on mount
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/auth/list/get-filters`);
        if (res.data?.[0]) {
          const data = res.data[0];
          setCategories(data.categories || []);
          setSubcategories(data.subCategories || []);
          setBrands(data.brands || []);
          const min = Math.min(...data.uniquePrices || [0]);
          const max = Math.max(...data.uniquePrices || [100000]);
          setMinVal(min);
          setMaxVal(50000);

          if (!searchParams.get('minPrice')) {
            setPriceRange([min, max > 50000 ? 100000 : max]);
            setValue([min, Math.min(max, 50000)]);
          }
        }
      } catch (err) {
        console.error("Failed to fetch metadata:", err);
      }

      try {
        const priceRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/auth/list/PriceRangeMaster`);
        setPriceRanges(priceRes.data || []);
      } catch (error) {
        console.error("Error fetching price ranges:", error);
      }
    };
    fetchMetadata();
  }, []);

  // Main data fetching effect - reacts to searchParams
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const page = parseInt(searchParams.get('page')) || 1;
        const skip = (page - 1) * per_page;
        const search = searchParams.get('search');

        // Normalize URL parameters
        let activeBrandIndices = searchParams.getAll('brands[]');
        const brand = searchParams.get('brand');
        if (brand && !activeBrandIndices.includes(brand)) {
          activeBrandIndices.push(brand);
        }

        let activeCategoriesIndices = searchParams.getAll('categories[]');
        const cat = searchParams.get('category');
        if (cat && !activeCategoriesIndices.includes(cat)) {
          activeCategoriesIndices.push(cat);
        }

        let activeSubCategoriesIndices = searchParams.getAll('subCategories[]');
        const subCat = searchParams.get('subCategory');
        if (subCat && !activeSubCategoriesIndices.includes(subCat)) {
          activeSubCategoriesIndices.push(subCat);
        }

        const minPrice = parseInt(searchParams.get('minPrice')) || minVal;
        const maxPrice = parseInt(searchParams.get('maxPrice')) || 100000;

        const sort = searchParams.get('sort') || '';

        // Base params for either filter or list
        const payload = {
          activeBrandIndices,
          activeCategoriesIndices,
          activeSubCategoriesIndices,
          value: [minPrice, maxPrice],
          skip,
          limit: per_page,
          sort,
          match: search || ""
        };

        // Sync local selection state for UI checkboxes
        setActiveBrands(activeBrandIndices);
        setActiveCategories(activeCategoriesIndices);
        setActiveSubCategories(activeSubCategoriesIndices);

        const currentCat = activeCategoriesIndices.slice().sort().join(',');
        const currentSearch = search || "";
        const currentSub = activeSubCategoriesIndices.slice().sort().join(',');
        // Refresh brand availability on category/search/sub-category change,
        // but not when only a brand is toggled.
        const brandScopeChanged =
          currentCat !== lastScopeRef.current.category ||
          currentSearch !== lastScopeRef.current.search ||
          currentSub !== lastScopeRef.current.subCategory;
        lastScopeRef.current = { category: currentCat, search: currentSearch, subCategory: currentSub };

        let endpoint = `${process.env.REACT_APP_API_URL}/api/auth/list/get-filtered-products`;

        const res = await axios.post(endpoint, payload);
        if (res.data?.products?.[0]) {
          const resultData = res.data.products[0];
          setProducts(resultData.products || []);
          setTotalPages(res.data.totalPages || 0);
          setActivePage(page);

          // Update both price states from result
          const resMin = resultData.minPrice ?? minVal;
          const resMax = resultData.maxPrice ?? 100000;
          setPriceRange([resMin, resMax]);
          setValue([resMin, Math.min(resMax, 50000)]);

          const currentBrands = (resultData.availableBrands || []).map((id) => id?.toString?.() || String(id));
          const currentSubCategories = (resultData.availableSubCategories || []).map((id) => id?.toString?.() || String(id));

          if (brandScopeChanged || activeBrandIndices.length === 0) {
            setCategoryBrands(currentBrands);
          }
          if (brandScopeChanged || activeSubCategoriesIndices.length === 0) {
            setCategorySubCategories(currentSubCategories);
          }
        } else {
          setProducts([]);
          setTotalPages(0);
          setActivePage(1);
          setPriceRange([minVal, 100000]);
          setValue([minVal, 50000]);
          if (brandScopeChanged || activeBrandIndices.length === 0) {
            setCategoryBrands([]);
          }
          if (brandScopeChanged || activeSubCategoriesIndices.length === 0) {
            setCategorySubCategories([]);
          }
        }
      } catch (err) {
        console.error("Error loading products:", err);
        setProducts([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [searchParams, minVal]); // Re-run when URL or default minVal changes



  const handlePageChange = (pageNumber) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', pageNumber);
    setSearchParams(params);
  };

  const handlePriceChange = (event, newValue) => {
    console.log(newValue)
    const cappedMin = Math.min(newValue[0], 49999);
    const actualMax = newValue[1] === 50000 ? 100000 : newValue[1];
    setPriceRange([cappedMin, actualMax]);
    setValue(newValue)
  };

  const applyPriceFilter = () => {
    const params = new URLSearchParams(searchParams);
    params.set('minPrice', priceRange[0]);
    params.set('maxPrice', priceRange[1]);
    params.set('page', 1); // Reset to page 1
    setSearchParams(params);
  };

  const handleSortChange = (e) => {
    const params = new URLSearchParams(searchParams);
    if (e.target.value) {
      params.set('sort', e.target.value);
    } else {
      params.delete('sort');
    }
    params.set('page', 1);
    setSearchParams(params);
  };

  const handleClearSort = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('sort');
    params.set('page', 1);
    setSearchParams(params);
  };

  const handleClick = (type, id) => {
    const params = new URLSearchParams(searchParams);
    const paramKey = `${type}[]`;

    // Singular param names mapping
    const singularMap = {
      categories: "category",
      subCategories: "subCategory",
      brands: "brand"
    };
    const singularKey = singularMap[type];

    // Convert singular parameter to plural list if present
    if (singularKey && params.has(singularKey)) {
      const singularVal = params.get(singularKey);
      params.delete(singularKey);
      params.delete("categoryName"); // Clean up label helper if category is toggled

      const currentPlurals = params.getAll(paramKey);
      if (!currentPlurals.includes(singularVal)) {
        params.append(paramKey, singularVal);
      }
    }

    let currentValues = params.getAll(paramKey);

    if (currentValues.includes(id)) {
      // Remove if exists
      const newValues = currentValues.filter(val => val !== id);
      params.delete(paramKey);
      newValues.forEach(val => params.append(paramKey, val));
    } else {
      // Add if new
      params.append(paramKey, id);
    }

    params.set('page', 1); // Reset to page 1
    setSearchParams(params);
  };

  const { setSearchText, setPriceRangeName } = useFilter();

  const handlePriceSelect = (price) => {
    const params = new URLSearchParams(searchParams);
    const isSelected = params.get('minPrice') === String(price.startPrice) && params.get('maxPrice') === String(price.endPrice);

    if (isSelected) {
      params.delete('minPrice');
      params.delete('maxPrice');
      params.delete('rangeName');
    } else {
      params.set('minPrice', price.startPrice);
      params.set('maxPrice', price.endPrice);
      params.set('rangeName', price.label);
    }
    params.set('page', 1); // Reset to page 1
    setSearchParams(params);
  };

  const handleClean = () => {
    setSearchParams({});
    setPriceRange([minVal, 100000]);
    setValue([minVal, 50000]);
    setSearchText("");
    setPriceRangeName("All Price");
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const getSelectedCategoryNames = () => {
    let activeCategoriesIndices = searchParams.getAll("categories[]");
    const cat = searchParams.get("category");
    if (cat && !activeCategoriesIndices.includes(cat)) {
      activeCategoriesIndices.push(cat);
    }
    const names = activeCategoriesIndices
      .map((id) => categories.find((c) => c._id === id)?.categoryName)
      .filter(Boolean);

    if (names.length === 0 && searchParams.get("categoryName")) {
      return [searchParams.get("categoryName")];
    }
    return names;
  };

  const hasCategoryScope = activeCategories.length > 0 || !!searchParams.get('search');

  const normalizeId = (id) => (id == null ? "" : id.toString());

  const isBrandChecked = (brandId) => activeBrands.some((id) => normalizeId(id) === normalizeId(brandId));
  const isSubCategoryChecked = (subId) => activeSubCategories.some((id) => normalizeId(id) === normalizeId(subId));
  const isCategoryChecked = (catId) => activeCategories.some((id) => normalizeId(id) === normalizeId(catId));

  const isBrandAvailable = (brandId) => {
    if (!hasCategoryScope) return true;
    return categoryBrands.includes(normalizeId(brandId));
  };

  const isSubCategoryAvailable = (item) => {
    if (!hasCategoryScope) return true;
    // Prefer master category link so newly selected categories unlock their subcats immediately
    if (activeCategories.length > 0) {
      const catId = item.categoryName?._id
        ? item.categoryName._id.toString()
        : (item.categoryName ? item.categoryName.toString() : "");
      if (activeCategories.some((id) => normalizeId(id) === catId)) return true;
    }
    return categorySubCategories.includes(normalizeId(item._id));
  };

  const getFilterItemClass = (isChecked, isAvailable) => {
    const classes = [];
    if (isChecked) classes.push("active");
    else classes.push("inactive");
    if (!isAvailable) classes.push("unavailable");
    return classes.join(" ");
  };

  const sortedBrands = React.useMemo(() => {
    return [...(brands || [])].sort((a, b) => {
      // 1. Brands under selected category first
      const aAvailable = isBrandAvailable(a._id);
      const bAvailable = isBrandAvailable(b._id);
      if (aAvailable && !bAvailable) return -1;
      if (!aAvailable && bAvailable) return 1;

      // 2. Checked brands next
      const aSelected = isBrandChecked(a._id);
      const bSelected = isBrandChecked(b._id);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;

      // 3. Alphabetical tie-breaker
      return (a.brandName || "").trim().localeCompare((b.brandName || "").trim());
    });
  }, [brands, activeBrands, categoryBrands, hasCategoryScope]);

  const sortedCategories = React.useMemo(() => {
    return [...(categories || [])].sort((a, b) => {
      // 1. Checked categories first
      const aSelected = isCategoryChecked(a._id);
      const bSelected = isCategoryChecked(b._id);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;

      // 2. Alphabetical tie-breaker
      return (a.categoryName || "").trim().localeCompare((b.categoryName || "").trim());
    });
  }, [categories, activeCategories]);

  const sortedSubCategories = React.useMemo(() => {
    return [...(subCategories || [])].sort((a, b) => {
      // 1. Available (under selected categories) first
      const aAvailable = isSubCategoryAvailable(a);
      const bAvailable = isSubCategoryAvailable(b);
      if (aAvailable && !bAvailable) return -1;
      if (!aAvailable && bAvailable) return 1;

      // 2. Checked next
      const aSelected = isSubCategoryChecked(a._id);
      const bSelected = isSubCategoryChecked(b._id);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;

      // 3. Alphabetical tie-breaker
      return (a.subCategoryName || "").trim().localeCompare((b.subCategoryName || "").trim());
    });
  }, [subCategories, activeSubCategories, activeCategories, categorySubCategories, hasCategoryScope]);

  return (
    <>
      {isLoading ? (
        <div className="loader-container">
          <Puff
            color="#a01e20"
            height={50}
            width={50}
            timeout={0}
          />
        </div>
      ) : (
        <div>

          <nav class="breadcrumb-nav mb-3">
            <div class="container">
              <ul className="breadcrumb">
                <li>
                  <Link to="/">Home</Link>
                </li>
                <li>
                  <Link to="/product-list">Product</Link>
                </li>
                {getSelectedCategoryNames().length > 0 && (
                  <li>
                    <Link to="#">
                      {getSelectedCategoryNames().join(" / ")}
                    </Link>
                  </li>
                )}
              </ul>
            </div>
          </nav>

          <div className="page-content mb-4">
            <div className="container">
              <Row className="shop-content row gutter-lg">
                <Col
                  xl={3}
                  lg={3}
                  md={12}
                  className="shop-sidebar sticky-sidebar-wrapper sidebar-fixed"
                >
                  <div className="sticky-sidebar">
                    <div class="filter-actions">
                      {(() => {
                        const hasFilters = activeBrands.length > 0 || activeCategories.length > 0 || activeSubCategories.length > 0 || !!searchParams.get('search');

                        return (
                          <>
                            <div>
                              <label>Filter :</label>
                              {isMobile && (
                                <div className="filterDiv d-inline-block ms-2 pointer" onClick={() => setShow(!show)} style={{ cursor: "pointer", fontSize: "20px", verticalAlign: "middle" }}>
                                  <FiFilter />
                                </div>
                              )}
                            </div>
                            {(!isMobile || show) && (
                              <button
                                onClick={handleClean}
                                className="btn btn-dark btn-link filter-clean"
                              >
                                Reset All
                              </button>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    {isMobile ? (
                      <div >
                        {show && (
                          <div>
                            {/* Commented out existing Price Range filter in sidebar
                            <Typography>
                              <h3 className="widget collapsed  text-start price-range-border">
                                <span className="widget-title">Price Range</span>
                              </h3>
                            </Typography>
                            <Accordion>
                              <AccordionDetails>
                                <Box>
                                  <div className="d-flex align-item-center justify-content-between">
                                    <div className="maxMinDiv">
                                      <p className="mb-0-p " style={{ textAlign: "start" }}>
                                        Min
                                      </p>
                                      <div className="d-flex justify-content-center">
                                        <input
                                          type="number"
                                          className="mb-0-p border-0"
                                          value={priceRange[0]}
                                          onChange={(e) => {
                                            const newMin = parseInt(e.target.value) || minVal;
                                            const cappedMin = Math.min(newMin, 49999);
                                            setPriceRange([Math.max(minVal, Math.min(cappedMin, priceRange[1])), priceRange[1]]);
                                          }}
                                          min={minVal}
                                          max={49999}
                                        />
                                      </div>
                                    </div>
                                    <div className="maxMinDiv">
                                      <p className="mb-0-p" style={{ textAlign: "start" }}>
                                        Max
                                      </p>
                                      <div className="d-flex justify-content-center">
                                        <input
                                          type="text"
                                          className="mb-0-p border-0"
                                          value={priceRange[1] >= 50000 ? "50000+" : priceRange[1]}
                                          onChange={(e) => {
                                            const inputVal = e.target.value;
                                            if (inputVal === "50000+") {
                                              setPriceRange([priceRange[0], 100000]);
                                            } else {
                                              const num = parseInt(inputVal) || priceRange[1];
                                              if (num > 50000) {
                                                setPriceRange([priceRange[0], 100000]);
                                              } else {
                                                setPriceRange([priceRange[0], Math.max(priceRange[0], num)]);
                                              }
                                            }
                                          }}
                                        />
                                      </div>
                                    </div>
                                  </div>


                                  <Slider
                                    getAriaLabel={() => "Price range"}
                                    min={minVal}
                                    max={maxVal}
                                    value={values}
                                    onChange={handlePriceChange}
                                    valueLabelDisplay="auto"
                                    getAriaValueText={valuetext}
                                    sx={{
                                      color: "#1976d2",
                                      "& .MuiSlider-thumb": { backgroundColor: "#a01e20" },
                                      "& .MuiSlider-track": { backgroundColor: "#a01e20", border: "1px solid #a01e20" },
                                      "& .MuiSlider-rail": { backgroundColor: "#a01e20" },
                                    }}
                                  />
                                  <button
                                    className="filter-btn"
                                    onClick={applyPriceFilter}
                                    type="button"
                                  >
                                    Apply Now
                                  </button>
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                            */}

                            {/* Dynamic Price Range filter same as topbar Price range modal */}
                            <Accordion
                              expanded={expanded === "priceRanges"}
                              onChange={handleAccordionChange("priceRanges")}
                              className="widget new border-0"
                            >
                              <AccordionSummary
                                expandIcon={
                                  <p
                                    className="px-3"
                                    style={{
                                      marginBottom: "0px",
                                      fontSize: "24px",
                                      fontWeight: "bolder",
                                      color: "rgb(58, 58, 58)",
                                    }}
                                  >
                                    {expanded === "priceRanges" ? <FiMinus /> : <FiPlus />}{" "}
                                  </p>
                                }
                                aria-controls="panelPrice-content"
                                id="panelPrice-header"
                              >
                                <Typography>
                                  <h3 className="widget-title collapsed">
                                    <span>Shop by Budget</span>
                                  </h3>
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <div className="sidebarPriceRangeContainer">
                                  {priceRanges?.map((item, index) => {
                                    const isSelected = searchParams.get('minPrice') === String(item.startPrice) && searchParams.get('maxPrice') === String(item.endPrice);
                                    return (
                                      <button
                                        key={index}
                                        className={`priceRangeItem ${isSelected ? "active" : ""}`}
                                        onClick={() => handlePriceSelect(item)}
                                      >
                                        {item.label}
                                      </button>
                                    );
                                  })}
                                </div>
                              </AccordionDetails>
                            </Accordion>

                            <Accordion
                              expanded={expanded === "brands"}
                              onChange={handleAccordionChange("brands")}
                              className="widget new border-0"
                            >
                              <AccordionSummary
                                expandIcon={
                                  <p
                                    className="px-3"
                                    style={{
                                      marginBottom: "0px",
                                      fontSize: "24px",
                                      fontWeight: "bolder",
                                      color: "rgb(58, 58, 58)",
                                    }}
                                  >
                                    {expanded === "brands" ? <FiMinus /> : <FiPlus />}{" "}

                                  </p>
                                }
                                aria-controls="panel3-content"
                                id="panel3-header"
                              >
                                <Typography>
                                  <h3 className="widget-title collapsed">
                                    <span>Shop by Brand</span>
                                  </h3>
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <ul className="widget-body filter-items item-check brandCard">
                                  {(() => {
                                    return sortedBrands?.map((item, index) => (
                                      <li
                                        key={index}
                                        className={getFilterItemClass(isBrandChecked(item._id), isBrandAvailable(item._id))}
                                        onClick={() => {
                                          if (!isBrandAvailable(item._id) && !isBrandChecked(item._id)) return;
                                          handleClick("brands", item._id);
                                        }}
                                      >
                                        <p className="p-0 text-left mb-1">
                                          {item.brandName}
                                        </p>
                                      </li>
                                    ));
                                  })()}
                                </ul>
                              </AccordionDetails>
                            </Accordion>

                            <Accordion
                              expanded={expanded === "subCategories"}
                              onChange={handleAccordionChange("subCategories")}
                              className="widget new border-0"
                            >
                              <AccordionSummary
                                expandIcon={
                                  <p
                                  className="px-3"
                                    style={{
                                      marginBottom: "0px",
                                      fontSize: "24px",
                                      fontWeight: "bolder",
                                      color: "rgb(58, 58, 58)",
                                    }}
                                  >
                                    {expanded === "subCategories" ? (
                                      <FiMinus />
                                    ) : (
                                      <FiPlus />
                                    )}{" "}

                                  </p>
                                }
                                aria-controls="panel2-content"
                                id="panel2-header"
                              >
                                <Typography>
                                  <h3 className="widget-title collapsed">
                                    <span> Sub Categories</span>
                                  </h3>
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <ul className="widget-body filter-items item-check brandCard">
                                  {(() => {
                                    return sortedSubCategories?.map((item, index) => (
                                      <li
                                        key={index}
                                        className={getFilterItemClass(isSubCategoryChecked(item._id), isSubCategoryAvailable(item))}
                                        onClick={() => {
                                          if (!isSubCategoryAvailable(item) && !isSubCategoryChecked(item._id)) return;
                                          handleClick("subCategories", item._id);
                                        }}
                                      >
                                        <p className="p-0 text-left mb-1">
                                          {item.subCategoryName}
                                        </p>
                                      </li>
                                    ));
                                  })()}
                                </ul>
                              </AccordionDetails>
                            </Accordion>
                            <Accordion
                              expanded={expanded === "categories"}
                              onChange={handleAccordionChange("categories")}
                              className="widget new border-0"
                            >
                              <AccordionSummary
                                expandIcon={
                                  <p
                                  className="px-3"
                                    style={{
                                      marginBottom: "0px",
                                      fontSize: "24px",
                                      fontWeight: "bolder",
                                      color: "rgb(58, 58, 58)",
                                    }}
                                  >
                                    {expanded === "categories" ? <FiMinus /> : <FiPlus />}{" "}

                                  </p>
                                }
                                aria-controls="panel1-content"
                                id="panel1-header"
                              >
                                <Typography>
                                  <h3 className="widget-title collapsed">
                                    <span>All Categories</span>
                                  </h3>
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <ul className="widget-body filter-items item-check brandCard">
                                  {(() => {
                                    return sortedCategories?.map((item, index) => (
                                      <li
                                        key={index}
                                        className={isCategoryChecked(item._id) ? "active" : "inactive"}
                                        onClick={() => handleClick("categories", item._id)}
                                      >
                                        <p className="p-0 text-left mb-1">
                                          {item.categoryName}
                                        </p>
                                      </li>
                                    ));
                                  })()}
                                </ul>
                              </AccordionDetails>
                            </Accordion>






                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        {/* Commented out existing Price Range filter in sidebar
                        <Typography>
                          <h3 className="widget collapsed  text-start price-range-border">
                            <span className="widget-title">Price Range</span>
                          </h3>
                        </Typography>
                        <Accordion>
                          <AccordionDetails>
                            <Box>
                              <div className="d-flex align-item-center justify-content-between">
                                <div className="maxMinDiv">
                                  <p className="mb-0-p " style={{ textAlign: "start" }}>
                                    Min
                                  </p>
                                  <div className="d-flex justify-content-center range-box w-auto">
                                    <input
                                      type="number"
                                      className="mb-0-p border-0"
                                      style={{ width: "70px" }}
                                      value={priceRange[0]}
                                      onChange={(e) => {
                                        const newMin = parseInt(e.target.value) || minVal;
                                        const cappedMin = Math.min(newMin, 49999);
                                        setPriceRange([Math.max(minVal, Math.min(cappedMin, priceRange[1])), priceRange[1]]);
                                      }}
                                      min={minVal}
                                      max={49999}
                                    />
                                  </div>
                                </div>
                                <div className="maxMinDiv">
                                  <p className="mb-0-p" style={{ textAlign: "start" }}>
                                    Max
                                  </p>
                                  <div className="d-flex justify-content-center range-box w-auto">
                                    <input
                                      type="text"
                                      className="mb-0-p border-0"
                                      style={{ width: "70px" }}
                                      value={priceRange[1] >= 50000 ? "50000+" : priceRange[1]}
                                      onChange={(e) => {
                                        const inputVal = e.target.value;
                                        if (inputVal === "50000+") {
                                          setPriceRange([priceRange[0], 100000]);
                                        } else {
                                          const num = parseInt(inputVal) || priceRange[1];
                                          if (num > 50000) {
                                            setPriceRange([priceRange[0], 100000]);
                                          } else {
                                            setPriceRange([priceRange[0], Math.max(priceRange[0], num)]);
                                          }
                                        }
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>

                              <Slider
                                getAriaLabel={() => "Temperature range"}
                                min={minVal}
                                max={maxVal}

                                value={values}
                                onChange={handlePriceChange}
                                valueLabelDisplay="auto"
                                getAriaValueText={valuetext}
                                sx={{
                                  color: "#1976d2",
                                  "& .MuiSlider-thumb": {
                                    backgroundColor: "#a01e20",
                                  },
                                  "& .MuiSlider-track": {
                                    backgroundColor: "#a01e20",
                                    border: "1px solid #a01e20",
                                  },
                                  "& .MuiSlider-rail": {
                                    backgroundColor: "#a01e20",
                                  },
                                }}
                              />
                              <button
                                className="filter-btn"
                                onClick={applyPriceFilter}
                                type="button"
                              >
                                Apply Now
                              </button>
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                        */}

                        {/* Dynamic Price Range filter same as topbar Price range modal */}
                        <Accordion
                          expanded={expanded === "priceRanges"}
                          onChange={handleAccordionChange("priceRanges")}
                          className="widget new border-0"
                        >
                          <AccordionSummary
                            expandIcon={
                              <p
                                style={{
                                  marginBottom: "0px",
                                  fontSize: "24px",
                                  fontWeight: "bolder",
                                  color: "rgb(58, 58, 58)",
                                }}
                              >
                                {expanded === "priceRanges" ? <FiMinus /> : <FiPlus />}{" "}
                              </p>
                            }
                            aria-controls="panelPrice-content"
                            id="panelPrice-header"
                          >
                            <Typography>
                              <h3 className="widget-title collapsed">
                                <span>Shop by Budget</span>
                              </h3>
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <div className="sidebarPriceRangeContainer">
                              {priceRanges?.map((item, index) => {
                                const isSelected = searchParams.get('minPrice') === String(item.startPrice) && searchParams.get('maxPrice') === String(item.endPrice);
                                return (
                                  <button
                                    key={index}
                                    className={`priceRangeItem ${isSelected ? "active" : ""}`}
                                    onClick={() => handlePriceSelect(item)}
                                  >
                                    {item.label}
                                  </button>
                                );
                              })}
                            </div>
                          </AccordionDetails>
                        </Accordion>

                        <Accordion
                          expanded={expanded === "brands"}
                          onChange={handleAccordionChange("brands")}
                          className="widget new border-0"
                        >
                          <AccordionSummary
                            expandIcon={
                              <p
                                style={{
                                  marginBottom: "0px",
                                  fontSize: "24px",
                                  fontWeight: "bolder",
                                  color: "rgb(58, 58, 58)",
                                }}
                              >
                                {expanded === "brands" ? <FiMinus /> : <FiPlus />}{" "}

                              </p>
                            }
                            aria-controls="panel3-content"
                            id="panel3-header"
                          >
                            <Typography>
                              <h3 className="widget-title collapsed">
                                <span>Shop by Brand</span>
                              </h3>
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <ul className="widget-body filter-items item-check brandCard scrollbarVisible">
                              {(() => {
                                return sortedBrands?.map((item, index) => (
                                  <li
                                    key={index}
                                    name="brands"
                                    className={getFilterItemClass(isBrandChecked(item._id), isBrandAvailable(item._id))}
                                    onClick={() => {
                                      if (!isBrandAvailable(item._id) && !isBrandChecked(item._id)) return;
                                      handleClick("brands", item._id);
                                    }}
                                  >
                                    <p className="p-0 text-left mb-1">{item.brandName}</p>
                                  </li>
                                ));
                              })()}

                            </ul>
                          </AccordionDetails>
                        </Accordion>

                        <Accordion
                          expanded={expanded === "subCategories"}
                          onChange={handleAccordionChange("subCategories")}
                          className="widget new border-0"
                        >
                          <AccordionSummary
                            expandIcon={
                              <p
                                style={{
                                  marginBottom: "0px",
                                  fontSize: "24px",
                                  fontWeight: "bolder",
                                  color: "rgb(58, 58, 58)",
                                }}
                              >
                                {expanded === "subCategories" ? (
                                  <FiMinus />
                                ) : (
                                  <FiPlus />
                                )}{" "}

                              </p>
                            }
                            aria-controls="panel2-content"
                            id="panel2-header"
                          >
                            <Typography>
                              <h3 className="widget-title collapsed">
                                <span> Sub Categories</span>
                              </h3>
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <ul className="widget-body filter-items item-check brandCard scrollbarVisible">
                              {(() => {
                                return sortedSubCategories?.map((item, index) => (
                                  <li
                                    key={index}
                                    name="subCategories"
                                    className={getFilterItemClass(isSubCategoryChecked(item._id), isSubCategoryAvailable(item))}
                                    onClick={() => {
                                      if (!isSubCategoryAvailable(item) && !isSubCategoryChecked(item._id)) return;
                                      handleClick("subCategories", item._id);
                                    }}
                                  >
                                    <p className="p-0 text-left mb-1">{item.subCategoryName}</p>
                                  </li>
                                ));
                              })()}

                            </ul>
                          </AccordionDetails>
                        </Accordion>
                        <Accordion
                          expanded={expanded === "categories"}
                          onChange={handleAccordionChange("categories")}
                          className="widget new border-0"
                        >
                          <AccordionSummary
                            expandIcon={
                              <p
                                style={{
                                  marginBottom: "0px",
                                  fontSize: "24px",
                                  fontWeight: "bolder",
                                  color: "rgb(58, 58, 58)",
                                }}
                              >
                                {expanded === "categories" ? <FiMinus /> : <FiPlus />}{" "}

                              </p>
                            }
                            aria-controls="panel1-content"
                            id="panel1-header"
                          >
                            <Typography>
                              <h3 className="widget-title collapsed">
                                <span>All Categories</span>
                              </h3>
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <ul className="widget-body filter-items item-check brandCard scrollbarVisible">
                              {(() => {
                                return sortedCategories?.map((item, index) => (
                                  <li
                                    key={index}
                                    id="categories"
                                    className={isCategoryChecked(item._id) ? "active" : "inactive"}
                                    onClick={() => handleClick("categories", item._id)}
                                  >
                                    <p className="p-0 text-left mb-1">
                                      {item.categoryName}
                                    </p>
                                  </li>
                                ));
                              })()}
                            </ul>
                          </AccordionDetails>
                        </Accordion>

                      </div>
                    )}

                  </div>
                </Col>
                <Col xl={9} lg={9} md={12} className="list-pagination">
                  <Row className="gy-4" >
                    <div className="dis-flex-end sortBy align-items-center">
                      <ProductInquiry />
                      <div className="d-flex align-items-center ms-3">
                        <div
                          className="toolbox-item toolbox-show select-box"
                          style={{ width: "215px" }}
                        >
                          <label>Sort By :</label>
                          <select
                            name="orderby"
                            className="form-control"
                            onChange={handleSortChange}
                            value={searchParams.get('sort') || ""}
                          >
                            <option value="">Select</option>
                            <option value="lowHigh">Price (low to high)</option>
                            <option value="highLow">Price (high to low)</option>
                            <option value="AZ">Sort by A to Z</option>
                            <option value="ZA">Sort by Z to A</option>
                          </select>
                        </div>
                        {searchParams.get('sort') && (
                          <button
                            type="button"
                            className="btn text-danger p-0 ms-2"
                            style={{
                              fontSize: "12px",
                              lineHeight: "1",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "3px",
                              textDecoration: "none",
                              border: "none",
                              outline: "none",
                              background: "transparent",
                              boxShadow: "none",
                              cursor: "pointer",
                              whiteSpace: "nowrap"
                            }}
                            onClick={handleClearSort}
                            title="Clear Sort"
                          >
                            <FiX size={12} /> Clear Sort
                          </button>
                        )}
                      </div>
                    </div>
                    {isLoading ? (
                      // Show loader when isLoading is true
                      <div className="loader-container" style={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                        <Puff
                          color="#a01e20"
                          height={50}
                          width={50}
                          timeout={0}
                        />
                      </div>
                    ) : products && products.length > 0 ? (
                      // If products exist, display the product list
                      products.map((items, index) => (
                        <Col lg={3} md={4} xs={6} sm={4} key={index}>
                          <div
                            className="item-card product-image-gap p-0 p-md-3"
                            onClick={() => setOpenProductId(items._id)}
                          >
                            <img
                              src={`${process.env.REACT_APP_API_URL}/${items.productImage}`}
                              alt={items.productName}
                            />
                            <p className="product-name mb-0 mt-5 px-1">
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setOpenProductId(items._id);
                                }}
                              >
                                {items.productName}
                              </a>
                            </p>
                            <p className="product-cat text-center m-1">
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setOpenProductId(items._id);
                                }}
                              >
                                {items.brandName.brandName}
                              </a>
                            </p>

                             <div className="product-single ">
                               <div className="product-price">
                                 <span className="mrp-label me-2">MRP: ₹</span>
                                 <span className="price-value">{items.newPrice}</span>
                               </div>
                             </div>


                            <ProductInquiry
                              data={items}
                              openModal={openProductId === items._id}
                              onCloseModal={() => setOpenProductId(null)}
                            />
                          </div>
                        </Col>
                      ))
                    ) : (
                      <div className="noProductMainDiv">
                        <div className="noProductTitle">
                          "No Products in this Category"
                        </div>
                      </div>
                    )}
                  </Row>
                  {products?.length > 0 && <Pagination>
                    <Pagination.Item
                      className="productListPrevBtn"
                      disabled={activePage === 1}
                      onClick={(e) => {
                        e.preventDefault();
                        if (activePage > 1) {
                          handlePageChange(activePage - 1);
                        }
                      }}
                    >
                      ‹
                    </Pagination.Item>
                    {(() => {
                      const pages = [];

                      if (isMobile) {
                        // Compact mobile layout: [Prev] 1 ... activePage-1 activePage activePage+1 ... totalPages [Next]
                        pages.push(
                          <Pagination.Item
                            style={{ border: "1px solid #ccc" }}
                            key={1}
                            active={activePage === 1}
                            onClick={() => handlePageChange(1)}
                          >
                            1
                          </Pagination.Item>
                        );

                        if (activePage > 3) {
                          pages.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
                        }

                        if (activePage - 1 > 1) {
                          pages.push(
                            <Pagination.Item
                              style={{ border: "1px solid #ccc" }}
                              key={activePage - 1}
                              active={false}
                              onClick={() => handlePageChange(activePage - 1)}
                            >
                              {activePage - 1}
                            </Pagination.Item>
                          );
                        }

                        if (activePage > 1 && activePage < totalPages) {
                          pages.push(
                            <Pagination.Item
                              style={{ border: "1px solid #ccc" }}
                              key={activePage}
                              active={true}
                              onClick={() => handlePageChange(activePage)}
                            >
                              {activePage}
                            </Pagination.Item>
                          );
                        }

                        if (activePage + 1 > 1 && activePage + 1 < totalPages) {
                          pages.push(
                            <Pagination.Item
                              style={{ border: "1px solid #ccc" }}
                              key={activePage + 1}
                              active={false}
                              onClick={() => handlePageChange(activePage + 1)}
                            >
                              {activePage + 1}
                            </Pagination.Item>
                          );
                        }

                        if (activePage < totalPages - 2) {
                          pages.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
                        }

                        if (totalPages > 1) {
                          pages.push(
                            <Pagination.Item
                              style={{ border: "1px solid #ccc" }}
                              key={totalPages}
                              active={activePage === totalPages}
                              onClick={() => handlePageChange(totalPages)}
                            >
                              {totalPages}
                            </Pagination.Item>
                          );
                        }
                      } else if (totalPages <= 10) {
                        // Show all pages if 10 or fewer
                        for (let i = 1; i <= totalPages; i++) {
                          pages.push(
                            <Pagination.Item
                              style={{ border: "1px solid #ccc" }}
                              key={i}
                              active={i === activePage}
                              onClick={() => handlePageChange(i)}
                            >
                              {i}
                            </Pagination.Item>
                          );
                        }
                      } else {
                        // Show first pages (3 on desktop, 1 on mobile)
                        const startLimit = isMobile ? 1 : 3;
                        for (let i = 1; i <= startLimit; i++) {
                          pages.push(
                            <Pagination.Item
                              style={{ border: "1px solid #ccc" }}
                              key={i}
                              active={i === activePage}
                              onClick={() => handlePageChange(i)}
                            >
                              {i}
                            </Pagination.Item>
                          );
                        }

                        // Show ellipsis if current page is far from start
                        if (activePage > (isMobile ? 3 : 6)) {
                          pages.push(
                            <Pagination.Ellipsis key="start-ellipsis" disabled />
                          );
                        }

                        // Show current page and surrounding pages (if not already shown)
                        const startPage = Math.max(isMobile ? 2 : 4, activePage - 1);
                        const endPage = Math.min(isMobile ? totalPages - 1 : totalPages - 3, activePage + 1);

                        for (let i = startPage; i <= endPage; i++) {
                          if (i > (isMobile ? 1 : 3) && i < (isMobile ? totalPages : totalPages - 2)) {
                            pages.push(
                              <Pagination.Item
                                style={{ border: "1px solid #ccc" }}
                                key={i}
                                active={i === activePage}
                                onClick={() => handlePageChange(i)}
                              >
                                {i}
                              </Pagination.Item>
                            );
                          }
                        }

                        // Show ellipsis if current page is far from end
                        if (activePage < (isMobile ? totalPages - 2 : totalPages - 5)) {
                          pages.push(
                            <Pagination.Ellipsis key="end-ellipsis" disabled />
                          );
                        }

                        // Show last pages (3 on desktop, 1 on mobile)
                        const endStart = isMobile ? totalPages : totalPages - 2;
                        for (let i = endStart; i <= totalPages; i++) {
                          pages.push(
                            <Pagination.Item
                              style={{ border: "1px solid #ccc" }}
                              key={i}
                              active={i === activePage}
                              onClick={() => handlePageChange(i)}
                            >
                              {i}
                            </Pagination.Item>
                          );
                        }
                      }

                      return pages;
                    })()}
                    <Pagination.Item
                      className="productListPrevBtn"
                      disabled={activePage === totalPages}
                      onClick={(e) => {
                        e.preventDefault();
                        if (activePage < totalPages) {
                          handlePageChange(activePage + 1);
                        }
                      }}
                    >
                      ›
                    </Pagination.Item>
                  </Pagination>}

                </Col>

              </Row>

            </div>
            <CreateCatalogBtn />
          </div>
        </div>
      )}
    </>
  );
};

export default ProductList;
